import type { CalendarDate } from '@internationalized/date';

function isSameCalendarYear(date: Date, today: Date): boolean {
  return date.getUTCFullYear() === today.getUTCFullYear();
}

/**
 * Format a timezone-free `CalendarDate` for display.
 *
 * Internally converts to a UTC `Date` so locale formatting is deterministic
 * regardless of the user's local timezone.
 *
 * The year is shown only when the date falls outside the current calendar year.
 *
 * | format     | current year | other year       |
 * | ---------- | ------------ | ---------------- |
 * | `'short'`  | `3/15`       | `3/15/23`        |
 * | `'medium'` | `Mar 15`     | `Mar 15, 2023`   |
 * | `'long'`   | `March 15, 2026` (always includes year) | same |
 */
export default function formatCalendarDate(
  calendarDate: CalendarDate,
  format: 'short' | 'medium' | 'long' = 'short'
): string {
  const date = new Date(Date.UTC(calendarDate.year, calendarDate.month - 1, calendarDate.day));
  const now = new Date();
  const omitYear = isSameCalendarYear(date, now);
  const dateYear = date.getUTCFullYear();

  if (format === 'short') {
    if (omitYear) {
      return date.toLocaleString('en-US', {
        month: 'numeric',
        day: 'numeric',
        timeZone: 'UTC',
      });
    }
    return date.toLocaleString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
      timeZone: 'UTC',
    });
  }

  if (format === 'medium') {
    const month = date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
    const day = date.getUTCDate();
    if (omitYear) {
      return `${month} ${day}`;
    }
    return `${month} ${day}, ${dateYear}`;
  }

  return date.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
